import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'src/utils/dayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import DropdownMenu from 'src/components/DropdownMenu';
import { useDispatch, useSelector } from 'react-redux';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  loadClientContracts,
  LOAD_CLIENT_CONTRACTS
} from 'src/store/actions/clientActions';
import { loadUpfrontRentNextPeriod } from 'src/store/actions/contractActions';
import {
  createUpfrontRentFromSantanderPaymentAction,
  CREATE_UPFRONT_RENT_SANTANDER
} from 'src/store/actions/santanderClabeActions';

import {
  loadParameter,
  LOAD_PARAMETER,
  CLEAR_PARAMETER
} from 'src/store/actions/parameterActions';
import { selectIndividualParameter } from 'src/store/selectors/parameterSelector';
import { selectClientContractList } from 'src/store/selectors/clientSelectors';
import { selectUpfrontRent } from 'src/store/selectors/contractSelectors';
import {
  getContractDescription,
  getUpfrontRentDisplayInfo,
  canSubmitUpfrontRent
} from 'src/utils/contracts';
import ContractStatus from 'src/utils/constants/contracts';
import Loading from 'src/components/Loading';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from '../ReconcilePaymentModal/styles';

const CreateUpfrontRentModal = ({ open, onClose, paymentData, reportId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const resetRef = useRef();
  const dispatch = useDispatch();
  const contracts = useSelector(selectClientContractList);
  const upfrontRent = useSelector(selectUpfrontRent);
  const toleranceAmount = Number(useSelector(selectIndividualParameter)) || 0;
  const isLoadingContracts = useSelector(
    isLoadingSelector([LOAD_CLIENT_CONTRACTS.action])
  );
  const isLoadingSubmit = useSelector(
    isLoadingSelector([CREATE_UPFRONT_RENT_SANTANDER.action])
  );
  const isLoadingTolerance = useSelector(
    isLoadingSelector([LOAD_PARAMETER.action])
  );
  const { unreconciledAmount } = paymentData;
  const initialState = useMemo(() => makeInitialState(), []);

  useEffect(() => {
    if (open && paymentData?.client?.id) {
      dispatch(
        loadClientContracts(
          { clientId: paymentData.client.id, status: ContractStatus.IN_PROGRESS, startAt: momentLocalTimezone().subtract(1, 'month').toISOString() },
          false
        )
      );
      dispatch(loadParameter('PAYMENT_TOLERANCE_AMOUNT'));
    }
    if (!open) {
      dispatch({ type: CLEAR_PARAMETER });
    }
  }, [dispatch, open, paymentData?.client?.id]);

  const handleFormSubmit = values => {
    dispatch(
      createUpfrontRentFromSantanderPaymentAction(
        paymentData.id,
        values.contract.id,
        values.continueBilling === 'true',
        onClose,
        reportId
      )
    );
  };

  const handleClose = () => {
    if (resetRef.current) {
      resetRef.current();
    }
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
      PaperProps={{
        sx: { width: '100%', maxWidth: 510 }
      }}
    >
      <DialogTitle className={classes.header}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            className={classes.header}
            color="primary"
            variant="h2"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {t('SANTANDER_REPORTS.CREATE_UPFRONT_RENT_MODAL_TITLE')}
          </Typography>
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={handleClose}
            className={classes.closeIcon}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoadingSubmit ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200
            }}
          >
            <Loading />
          </Box>
        ) : (
          <Formik
            initialValues={initialState}
            enableReinitialize
            validationSchema={validationSchema()}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleSubmit,
              touched,
              values,
              resetForm,
              setFieldValue
            }) => {
              resetRef.current = () => resetForm();
              const { upfrontRentAmount, rentExceedsUnreconciled, toleranceApplied } =
                getUpfrontRentDisplayInfo(values.contract, unreconciledAmount, toleranceAmount);
              const upFrontError =
                upfrontRent &&
                Number(upfrontRent.period) > Number(upfrontRent.totalPeriods);
              const canSubmit = canSubmitUpfrontRent(
                values.contract,
                rentExceedsUnreconciled,
                isLoadingSubmit || isLoadingTolerance
              ) && !!values.continueBilling && !upFrontError;

              return (
                <Form onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  {rentExceedsUnreconciled && (
                    <Alert
                      severity="warning"
                      className={classes.warningAlert}
                      sx={{ width: '100%', marginBottom: 2 }}
                    >
                      {t(
                        'SANTANDER_REPORTS.UNRECONCILED_LESS_THAN_RENT_WARNING'
                      )}
                    </Alert>
                  )}

                  {toleranceApplied && (
                    <Alert severity="info" sx={{ width: '100%', marginBottom: 2 }}>
                      {t('SANTANDER_REPORTS.TOLERANCE_APPLIED', {
                        amount: formatAmountWithDecimals(
                          upfrontRentAmount - unreconciledAmount,
                          2
                        )
                      })}
                    </Alert>
                  )}

                  <Autocomplete
                    sx={{ marginTop: 2, width: '100%' }}
                    options={contracts || []}
                    getOptionLabel={option =>
                      option ? getContractDescription(option) : ''
                    }
                    value={values.contract}
                    onChange={(event, value) => {
                      setFieldValue('contract', value);
                      setFieldValue('continueBilling', '');
                      if (value?.id) {
                        dispatch(loadUpfrontRentNextPeriod({ id: value.id }));
                      }
                    }}
                    loading={isLoadingContracts}
                    loadingText={t('COMMON.LOADING')}
                    disabled={isLoadingContracts}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('COMMON.CONTRACT')}
                        variant="outlined"
                        placeholder={t('COMMON.SEARCH')}
                        error={Boolean(touched.contract && errors.contract)}
                        helperText={
                          touched.contract && errors.contract
                            ? t(errors.contract)
                            : ''
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body1">
                          {getContractDescription(option)}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={t('SANTANDER_REPORTS.NO_CONTRACTS_FOUND')}
                  />

                  {values.contract && upfrontRent && !upFrontError && (
                    <TextField
                      fullWidth
                      sx={{ marginTop: 2 }}
                      InputProps={{ readOnly: true }}
                      label={t('CONTRACT_VIEW.UPFRONT_RENT_DATE')}
                      value={t('CONTRACT_VIEW.UPFRONT_RENT_PERIOD_LABEL', {
                        period: upfrontRent.period,
                        total: upfrontRent.totalPeriods,
                        date: selectFormatDate(
                          dayjs(upfrontRent.date, 'MM-YYYY'),
                          dateFormatType.MONTH
                        )
                      })}
                      disabled
                      variant="outlined"
                    />
                  )}

                  {upFrontError && (
                    <Alert
                      severity="error"
                      className={classes.warningAlert}
                      sx={{ width: '100%', marginTop: 2 }}
                    >
                      {t('SCHEMA_ERRORS.UPFRONT_RENT_LIMIT')}
                    </Alert>
                  )}

                  {values.contract && (
                    <>
                      <TextField
                        fullWidth
                        sx={{ marginTop: 2 }}
                        label={t('SANTANDER_REPORTS.UPFRONT_RENT_AMOUNT')}
                        value={formatAmountWithDecimals(upfrontRentAmount, 2)}
                        disabled
                        variant="outlined"
                      />
                      <FormControl fullWidth sx={{ marginTop: 2 }}>
                        <DropdownMenu
                          list={[
                            { key: 'true', name: 'COMMON.YES' },
                            { key: 'false', name: 'COMMON.NO' }
                          ]}
                          value={values.continueBilling}
                          setValue={value =>
                            setFieldValue('continueBilling', value)
                          }
                          label={t(
                            'SANTANDER_REPORTS.CONTINUE_BILLING_NEXT_MONTH'
                          )}
                          error={Boolean(
                            touched.continueBilling && errors.continueBilling
                          )}
                          errorText={
                            touched.continueBilling && errors.continueBilling
                              ? t(errors.continueBilling)
                              : ''
                          }
                        />
                      </FormControl>
                    </>
                  )}

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      margin: '1rem auto'
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      color="primary"
                      variant="outlined"
                      sx={{ minWidth: '45%' }}
                      disabled={isLoadingSubmit}
                    >
                      {t('COMMON.CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: '45%' }}
                      disabled={!canSubmit || isLoadingContracts}
                    >
                      {isLoadingSubmit
                        ? t('COMMON.LOADING')
                        : t('SANTANDER_REPORTS.CREATE_UPFRONT_RENT')}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreateUpfrontRentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    amount: PropTypes.number.isRequired,
    unreconciledAmount: PropTypes.number.isRequired,
    client: PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullname: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  reportId: PropTypes.string.isRequired
};

export default CreateUpfrontRentModal;
